import { useMemo } from 'react';

import { useOrderTruckTracking, useOrderTrailerTracking } from '../models';
import { useTrackingLogs } from './useTrackingLogs';
import type { OrderTrackingEvent } from '../endpoints/OrderTrackingEventsEndpoint.types';

export interface OrderMapLocation {
  trackingEvents: OrderTrackingEvent[];
  driverLocation: [number, number] | null;
  driverHeading: number | null;
  /** When the truck last reported the position behind the marker — set only when that marker is telemetry. */
  telemetryLastSeenAt: string | null;
  /** The trailer on the order, when it has one and this viewer may see where it is. */
  trailerLocation: [number, number] | null;
  /** Which way the trailer's unit says it is heading — the truck pulling it, in practice. */
  trailerHeading: number | null;
  /** The trailer's plate, so its marker and its "last signal" line can name what they point at. */
  trailerPlate: string | null;
  /** When the trailer last reported the position behind its marker. */
  trailerLastSeenAt: string | null;
}

/**
 * What an order's map draws: where the truck is, which way it points, and where the trailer is.
 *
 * Two sources can answer for the truck. The driver app pings per order, but only while a phone is running
 * it; a tracking connection reports the vehicle itself, with no phone involved. The connection wins whenever
 * the order's truck has one — it is the same truck, reported by the thing that exists to report it — and
 * the driver app answers for the orders no connection covers.
 *
 * The trailer is reported by the connection that follows it, and only ever that: nothing else knows where a
 * trailer is. Whether this viewer may see it is the server's answer, not this hook's — a customer whose order
 * is done is answered with no trailer, and nothing is drawn.
 *
 * `telemetryLastSeenAt` and `trailerLastSeenAt` say how old each marker is, so the map can say so rather
 * than passing a stale position off as a live one.
 */
export function useOrderMapLocation(orderId: string): OrderMapLocation {
  const { trackingEvents, driverLocation: driverPingLocation } = useTrackingLogs(orderId);
  const { data } = useOrderTruckTracking(orderId);
  const { data: trailerData } = useOrderTrailerTracking(orderId);

  const position = data?.position ?? null;
  const trailer = trailerData?.trailer ?? null;

  const driverLocation = useMemo<[number, number] | null>(
    () => (position ? [position.longitude, position.latitude] : driverPingLocation),
    [position, driverPingLocation],
  );

  // Memoised, and for a reason beyond tidiness: a fresh tuple on every render would give the map's bounds a
  // new identity each time, and fitting those bounds again would fight whatever the user just panned to.
  const trailerLocation = useMemo<[number, number] | null>(
    () => (trailer ? [trailer.position.longitude, trailer.position.latitude] : null),
    [trailer],
  );

  return {
    trackingEvents,
    driverLocation,
    driverHeading: position?.heading ?? null,
    telemetryLastSeenAt: position?.recordedAt ?? null,
    trailerLocation,
    trailerHeading: trailer?.position.heading ?? null,
    trailerPlate: trailer?.licensePlate ?? null,
    trailerLastSeenAt: trailer?.position.recordedAt ?? null,
  };
}
